// Shared entity-classification & filtering helpers used by all county adapters.
// Centralizing this logic eliminates per-county duplication and makes the
// filtering rules consistent everywhere.

// Word-boundary bank keywords — generic terms that must not match inside
// unrelated words (e.g. "LOAN" should not match "LOANE").
export const BANK_KEYWORDS = [
  'BANK', 'MORTGAGE', 'CREDIT UNION', 'FEDERAL SAVINGS', 'LOAN',
  'FINANCIAL', 'LENDING', 'CAPITAL', 'FUNDING', 'FANNIE', 'FREDDIE',
  'HUD', 'VETERANS', 'FHA', 'RURAL DEVELOPMENT', 'NATIONSTAR',
  'QUICKEN', 'ROCKET', 'WELLS FARGO', 'CHASE', 'CITIMORTGAGE',
  'MERS', 'FINANCE OF AMERICA', 'AMERIHOME', 'CALIBER', 'LOANDEPOT',
  'PENNYMAC', 'CARRINGTON', 'FREEDOM MORTGAGE', 'NEW AMERICAN',
  'HOUSING CORPORATION', 'HOUSING FINANCE', 'AFFORDABLE HOUSING',
  'STATE HOUSING', 'HOUSING AUTHORITY', 'HOUSING AGENCY',
  'SECRETARY OF HOUSING', 'NOMINEE',
];

// Substring bank keywords — match even when fused into a single token
// (e.g. "PRIMELENDING" contains "LENDING"; "GUARANTEED RATE" naming).
export const BANK_SUBSTRINGS = [
  'LENDING', 'MORTG', 'GUARANTEED RATE', 'PRIMELEND', 'CITIMORT',
  'BANCORP', 'BANCSHARES',
];

export const BUSINESS_KEYWORDS = [
  'LLC', 'INC', 'CORP', 'COMPANY', 'LTD', 'HOLDINGS', 'PROPERTIES',
  'ENTERPRISES', 'ASSOCIATES', 'GROUP', 'PARTNERS', 'DEVELOPMENT',
  'INVESTMENTS', 'REALTY', 'REAL ESTATE', 'CONSTRUCTION', 'BUILDERS',
  'SERVICES', 'MANAGEMENT', 'SOLUTIONS',
];

export const EXCLUDED_KEYWORDS = [
  'CHURCH', 'PARISH', 'MINISTRY', 'MINISTRIES', 'CONGREGATION', 'IRA',
];

export const TRUST_KEYWORDS = [
  'TRUST', 'TRUSTEE', 'REVOCABLE', 'IRREVOCABLE', 'LIVING TRUST',
];

const hasKeyword = (name: string, keywords: string[]): boolean => {
  if (!name) return false;
  const upper = name.toUpperCase();
  // Use word boundaries to avoid matching e.g. "INCLUDE" as "INC".
  return keywords.some((kw) => new RegExp(`\\b${kw}\\b`).test(upper));
};

const hasSubstring = (name: string, substrings: string[]): boolean => {
  if (!name) return false;
  const upper = name.toUpperCase();
  return substrings.some((s) => upper.includes(s));
};

export const isExcludedEntity = (name: string): boolean =>
  hasKeyword(name, EXCLUDED_KEYWORDS);

export const isBankEntity = (name: string): boolean =>
  hasKeyword(name, BANK_KEYWORDS) || hasSubstring(name, BANK_SUBSTRINGS);

export const isBusinessEntity = (name: string): boolean =>
  hasKeyword(name, BUSINESS_KEYWORDS) || hasKeyword(name, TRUST_KEYWORDS);

export const extractLastName = (name: string): string => {
  const cleaned = name.trim().toUpperCase();
  if (!cleaned) return '';
  if (cleaned.includes(',')) {
    return cleaned.split(',')[0].trim().split(/\s+/)[0];
  }
  // Many county feeds put the last name FIRST (e.g. "SMITH JOHN R").
  return cleaned.split(/\s+/)[0];
};

export const isFamilyTransfer = (
  grantors: string[],
  grantees: string[],
): boolean => {
  if (grantors.some((g) => isBusinessEntity(g) || isBankEntity(g))) return false;
  if (grantees.some((g) => isBusinessEntity(g) || isBankEntity(g))) return false;
  for (const g of grantors) {
    const gLast = extractLastName(g);
    if (gLast.length <= 1) continue;
    for (const e of grantees) {
      if (extractLastName(e) === gLast) return true;
    }
  }
  return false;
};

export const isBusinessToBusinessTransfer = (
  grantors: string[],
  grantees: string[],
): boolean => {
  if (grantors.length === 0 || grantees.length === 0) return false;
  const allG = grantors.every((g) => isBusinessEntity(g) || isBankEntity(g));
  const allE = grantees.every((g) => isBusinessEntity(g) || isBankEntity(g));
  return allG && allE;
};
